// A global run is not evidence that every current subject was included in it.
export function hasSubjectScreening(result, subject) {
  if (!result) return false;
  const same = entry => entry && (entry.key ? entry.key === subject.key
    : String(entry.name || '').trim().toLowerCase() === String(subject.name || '').trim().toLowerCase());
  return !!(result.subjects?.some(same) || result.results?.some(entry => same(entry.subject)) || result.errors?.some(same));
}

export function unchangedScreeningSubjects(previous, current) {
  const signature = subject => JSON.stringify([subject.name, ...['subjectType', 'givenNames', 'familyName', 'dateOfBirth', 'nationality', 'domicileCountry'].map(key => subject.meta?.[key] ?? '')]);
  return current.filter(subject => previous.some(old => old.key === subject.key && signature(old) === signature(subject)));
}

export function retainSubjectScreening(result, subjects) {
  if (!result) return null;
  const includes = entry => subjects.some(subject => entry?.key ? entry.key === subject.key : entry?.name === subject.name);
  const retained = {
    ...result,
    subjects: (result.subjects || []).filter(includes),
    results: (result.results || []).filter(entry => includes(entry.subject)),
    errors: (result.errors || []).filter(includes),
    matches: (result.matches || []).filter(match => includes({ key: match.__subjectKey, name: match.__subjectName || match.subjectName })),
  };
  return retained.subjects.length || retained.results.length || retained.errors.length || retained.matches.length ? retained : null;
}

// Only explicit successful empty responses can automatically clear a subject.
// null means PEP research is still pending; [] means no subject can be cleared.
export function noMatchSubjectKeys(kind, subjects, result, webResearch) {
  if (!result) return [];
  const needsWebResearch = subject => String(subject.meta?.subjectType || '').toLowerCase() !== 'entity';
  if (kind === 'pep' && !webResearch?.refreshFailed && !subjects.filter(needsWebResearch).every(subject =>
    webResearch?.items?.some(item => item.subjectKey === subject.key && item.result)
  )) return null;
  return subjects.filter(subject => {
    if (result.errors?.some(error => error.key === subject.key || error.name === subject.name)) return false;
    if (kind === 'sanctions') {
      const response = result.results?.find(item => item.subject?.key === subject.key);
      return response?.ok === true && Array.isArray(response.result?.matches) && response.result.matches.length === 0;
    }
    const database = result.subjects?.find(item => item.key === subject.key)?.response;
    const web = webResearch?.items?.find(item => item.subjectKey === subject.key);
    if (!Array.isArray(database?.results) || database.results.length !== 0) return false;
    // Public-web PEP research runs only for individuals in the existing pipeline.
    if (!needsWebResearch(subject)) return true;
    return !webResearch?.refreshFailed && !webResearch?.researchIncomplete && web?.result?.researchStatus !== 'partial' && !web?.previousResearch && !!web?.result &&
      (Array.isArray(web.result.candidates) ? web.result.candidates.length === 0 : web.result.category === 'not_established');
  }).map(subject => subject.key);
}

// An exposure outcome needs evidence belonging to this subject, or a written reason.
export function isSubjectReviewComplete(decision, hasSelectedEvidence, supportingInformation) {
  return decision === "no_exposure" || (
    decision === "exposure" && (
      hasSelectedEvidence ||
      (typeof supportingInformation === "string" && supportingInformation.trim().length > 0)
    )
  );
}
